import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import Lenis from 'lenis'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { prefersReducedMotion } from '@/lib/animation-config'

gsap.registerPlugin(ScrollTrigger)

let lenisInstance: Lenis | null = null

/**
 * Initialize Lenis smooth scrolling with GSAP ScrollTrigger integration
 * Only initializes once globally (singleton pattern)
 */
export function useLenis() {
  const location = useLocation()

  useEffect(() => {
    // Skip for reduced motion preference
    if (prefersReducedMotion()) {
      return
    }

    // Only create one instance
    if (lenisInstance) {
      return
    }

    lenisInstance = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: 'vertical',
      gestureOrientation: 'vertical',
      smoothWheel: true,
      touchMultiplier: 2,
    })

    // Connect Lenis scroll to ScrollTrigger
    lenisInstance.on('scroll', ScrollTrigger.update)

    // Mark GSAP as ready so CSS knows to let GSAP control animations
    // Small delay to ensure ScrollTrigger has time to set up
    requestAnimationFrame(() => {
      document.documentElement.classList.add('gsap-ready')
      ScrollTrigger.refresh()
    })

    // Integrate with GSAP ticker for consistent timing
    gsap.ticker.add((time) => {
      lenisInstance?.raf(time * 1000)
    })

    // Disable GSAP's lagSmoothing for Lenis compatibility
    gsap.ticker.lagSmoothing(0)

    return () => {
      if (lenisInstance) {
        document.documentElement.classList.remove('gsap-ready')
        lenisInstance.off('scroll', ScrollTrigger.update)
        gsap.ticker.remove(lenisInstance.raf)
        lenisInstance.destroy()
        lenisInstance = null
      }
    }
  }, [])

  // Refresh ScrollTrigger on route changes and scroll to top
  useEffect(() => {
    // Small delay to let React render the new page
    const timeout = setTimeout(() => {
      // Scroll to top on route change
      lenisInstance?.scrollTo(0, { immediate: true })
      window.scrollTo(0, 0)

      // Refresh ScrollTrigger to recalculate positions
      ScrollTrigger.refresh()
    }, 100)

    return () => clearTimeout(timeout)
  }, [location.pathname])

  return lenisInstance
}

/**
 * Get the global Lenis instance (for programmatic scrolling)
 */
export function getLenis(): Lenis | null {
  return lenisInstance
}
